import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from 'config/axiosBaseQuery';
import { endpoints } from 'config/api';
import { Budget, CreateBudgetDto, UpdateBudgetDto, UpdateBudgetStatusDto } from './budgetsTypes';
import * as Sentry from '@sentry/react';

export const budgetsApi = createApi({
  reducerPath: 'budgetsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Budget', 'ProjectBudgets', 'Project', 'Projects', 'Lead', 'Leads'],
  endpoints: (builder) => ({
    createBudget: builder.mutation<Budget, CreateBudgetDto>({
      query: (budgetData) => ({
        url: endpoints.budgets.base,
        method: 'POST',
        data: budgetData,
      }),
      invalidatesTags: ['Budget', 'ProjectBudgets', 'Project', 'Projects', 'Lead', 'Leads'],
      async onQueryStarted(budgetData, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          Sentry.captureException(error?.error || error, {
            tags: { operation: 'budget_create', api: 'budgetsApi' },
            extra: { costItemCount: budgetData?.costItems?.length || 0 },
          });
        }
      },
    }),

    updateBudget: builder.mutation<Budget, { budgetId: string; budgetData: UpdateBudgetDto }>({
      query: ({ budgetId, budgetData }) => ({
        url: endpoints.budgets.update(budgetId),
        method: 'PUT',
        data: budgetData,
      }),
      invalidatesTags: ['Budget', 'ProjectBudgets', 'Project', 'Projects', 'Lead', 'Leads'],
      async onQueryStarted({ budgetId, budgetData }, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          Sentry.captureException(error?.error || error, {
            tags: { operation: 'budget_update', api: 'budgetsApi' },
            extra: { budgetId, costItemCount: budgetData?.costItems?.length || 0 },
          });
        }
      },
    }),

    updateBudgetStatus: builder.mutation<Budget, { budgetId: string; status: UpdateBudgetStatusDto }>({
      query: ({ budgetId, status }) => ({
        url: endpoints.budgets.status(budgetId),
        method: 'PATCH',
        data: status,
      }),
      invalidatesTags: ['Budget', 'ProjectBudgets', 'Project', 'Projects', 'Lead', 'Leads'],
      async onQueryStarted({ budgetId, status }, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          Sentry.captureException(error?.error || error, {
            tags: { operation: 'budget_status_update', api: 'budgetsApi' },
            extra: { budgetId, newStatus: status.status },
          });
        }
      },
    }),

    getBudget: builder.query<Budget, string>({
      query: (budgetId) => ({
        url: endpoints.budgets.details(budgetId),
        method: 'GET',
      }),
      providesTags: (_res, _err, budgetId) => [{ type: 'Budget', id: budgetId }],
    }),

    getProjectBudgets: builder.query<Budget[], string>({
      query: (projectId) => ({
        url: endpoints.budgets.byProject(projectId),
        method: 'GET',
      }),
      providesTags: (_res, _err, projectId) => [{ type: 'ProjectBudgets', id: projectId }],
    }),

    getLeadBudgets: builder.query<Budget[], string>({
      query: (leadId) => ({
        url: endpoints.budgets.byLead(leadId),
        method: 'GET',
      }),
      providesTags: (_res, _err, leadId) => [{ type: 'Lead', id: leadId }],
    }),

    setPrimaryBudget: builder.mutation<Budget, { budgetId: string; projectId?: string; leadId?: string }>({
      query: ({ budgetId, projectId, leadId }) => ({
        url: endpoints.budgets.setPrimary(budgetId),
        method: 'PATCH',
        data: { projectId, leadId },
      }),
      invalidatesTags: ['Budget', 'ProjectBudgets', 'Project', 'Projects', 'Lead', 'Leads'],
      async onQueryStarted({ budgetId, projectId, leadId }, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          Sentry.captureException(error?.error || error, {
            tags: { operation: 'budget_set_primary', api: 'budgetsApi' },
            extra: { budgetId, projectId, leadId },
          });
        }
      },
    }),

    deleteBudget: builder.mutation<void, string>({
      query: (budgetId) => ({
        url: endpoints.budgets.delete(budgetId),
        method: 'DELETE',
      }),
      invalidatesTags: ['Budget', 'ProjectBudgets', 'Project', 'Projects', 'Lead', 'Leads'],
      async onQueryStarted(budgetId, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          Sentry.captureException(error?.error || error, {
            tags: { operation: 'budget_delete', api: 'budgetsApi' },
            extra: { budgetId },
          });
        }
      },
    }),
  }),
});

export const {
  useCreateBudgetMutation,
  useUpdateBudgetMutation,
  useUpdateBudgetStatusMutation,
  useGetBudgetQuery,
  useLazyGetBudgetQuery,
  useGetProjectBudgetsQuery,
  useGetLeadBudgetsQuery,
  useSetPrimaryBudgetMutation,
  useDeleteBudgetMutation,
} = budgetsApi;
